// src/pages/Dashboard/ProfilePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { api, apiIdp, refreshRolesJwt } from "../../api/client";
import "./ProfilePage.scss";

type MeResponse = {
    id: number;
    phone: string | null;
    name: string | null;
    email: string | null;
    roles: string[];
    primaryRole: string;
    avatarUrl: string | null;
};

type RolesResponse = {
    roles: string[];
};

type UserRole = {
    code: string;
    name: string;
    hasRole: boolean;
};

const KNOWN_ROLE_CODES = [
    "ROLE_USER",
    "ROLE_CLIENT",
    "ROLE_STAFF",
    "ROLE_MANAGER",
    "ROLE_FR",
    "ROLE_FRANCHISEE",
    "ROLE_YK",
    "ROLE_SPECIALIST",
    "ROLE_SPECIALIST_TS",
    "ROLE_DESIGN",
    "ROLE_SUPPORT",
    "ROLE_ADMIN",
    "ROLE_SUPER_ADMIN",
    "ROLE_EXTERNAL_CASHBOX",
] as const;

const CLIENT_ROLE_CODES = ["ROLE_USER", "ROLE_CLIENT"];
const STAFF_ROLE_CODES = ["ROLE_STAFF"];
const CURRENT_APP_ROLE_CODES = ["ROLE_FR", "ROLE_FRANCHISEE"];
const SUPPORT_ROLE_CODES = [
    "ROLE_MANAGER",
    "ROLE_SPECIALIST",
    "ROLE_SPECIALIST_TS",
    "ROLE_DESIGN",
    "ROLE_SUPPORT",
    "ROLE_ADMIN",
    "ROLE_SUPER_ADMIN",
    "ROLE_YK",
    "ROLE_EXTERNAL_CASHBOX",
];
const CURRENT_APP_ACTION_CODE = "__CURRENT_APP__";
const CURRENT_APP_STAY_LABEL = "Остаться в кабинете франчайзи";

const roleDisplay: Record<string, string> = {
    ROLE_USER: "Клиент",
    ROLE_CLIENT: "Клиент",
    ROLE_STAFF: "Мастер",
    ROLE_MANAGER: "Менеджер",
    ROLE_FR: "Франчайзи",
    ROLE_FRANCHISEE: "Франчайзи",
    ROLE_YK: "УК",
    ROLE_SPECIALIST: "Специалист",
    ROLE_SPECIALIST_TS: "Сотрудник Тех. Поддержки",
    ROLE_DESIGN: "Дизайнер",
    ROLE_SUPPORT: "Поддержка",
    ROLE_ADMIN: "Администратор",
    ROLE_SUPER_ADMIN: "Супер администратор",
    ROLE_EXTERNAL_CASHBOX: "Внешняя касса",
};

function buildRoleList(rolesFromApi: string[]): UserRole[] {
    const normalizedRoles = Array.isArray(rolesFromApi)
        ? Array.from(new Set(rolesFromApi.map((role) => String(role))))
        : [];

    const knownRoles = KNOWN_ROLE_CODES.map((code) => ({
        code,
        name: roleDisplay[code] || code,
        hasRole: normalizedRoles.includes(code),
    }));

    const unknownRoles = normalizedRoles
        .filter((code) => !KNOWN_ROLE_CODES.includes(code as (typeof KNOWN_ROLE_CODES)[number]))
        .map((code) => ({
            code,
            name: roleDisplay[code] || code,
            hasRole: true,
        }));

    return [...knownRoles, ...unknownRoles];
}

function trimTrailingSlash(value: string | undefined | null): string {
    return String(value || "").trim().replace(/\/$/, "");
}

function getFallbackTargets() {
    if (typeof window === "undefined") {
        return {
            client: "/",
            staff: "/",
            lkfr: "/",
            support: "/",
        };
    }

    const protocol = window.location.protocol === "http:" ? "http:" : "https:";
    const host = window.location.hostname.toLowerCase();
    const currentOrigin = trimTrailingSlash(window.location.origin) || "/";

    if (host.endsWith(".devchio.ru")) {
        return {
            client: `${protocol}//widget.devchio.ru`,
            staff: `${protocol}//staff.devchio.ru`,
            lkfr: `${protocol}//lkfr.devchio.ru`,
            support: `${protocol}//support.devchio.ru`,
        };
    }

    if (host.endsWith(".chio-chio.ru")) {
        return {
            client: `${protocol}//widget2.chio-chio.ru`,
            staff: `${protocol}//staff.chio-chio.ru`,
            lkfr: `${protocol}//lkfr.chio-chio.ru`,
            support: `${protocol}//designfront.chio-chio.ru`,
        };
    }

    return {
        client: currentOrigin,
        staff: currentOrigin,
        lkfr: currentOrigin,
        support: currentOrigin,
    };
}

function getAppTargets() {
    const fallbackTargets = getFallbackTargets();

    return {
        client: trimTrailingSlash(process.env.REACT_APP_CLIENT_HOME_URL) || fallbackTargets.client,
        staff: trimTrailingSlash(process.env.REACT_APP_STAFF_HOME_URL) || fallbackTargets.staff,
        lkfr: trimTrailingSlash(process.env.REACT_APP_LKFR_HOME_URL) || fallbackTargets.lkfr,
        support: trimTrailingSlash(process.env.REACT_APP_SUPPORT_HOME_URL) || fallbackTargets.support,
    };
}

function resolveRoleTarget(
    roleCode: string,
    appTargets: ReturnType<typeof getAppTargets>
): string {
    if (roleCode === CURRENT_APP_ACTION_CODE) return appTargets.lkfr;
    if (CLIENT_ROLE_CODES.includes(roleCode)) return appTargets.client;
    if (STAFF_ROLE_CODES.includes(roleCode)) return appTargets.staff;
    if (CURRENT_APP_ROLE_CODES.includes(roleCode)) return appTargets.lkfr;
    if (SUPPORT_ROLE_CODES.includes(roleCode)) return appTargets.support;
    return appTargets.client;
}

export const ProfilePage: React.FC = () => {
    const [me, setMe] = useState<MeResponse | null>(null);
    const [meLoading, setMeLoading] = useState(true);
    const [meError, setMeError] = useState<string | null>(null);
    const [userRoles, setUserRoles] = useState<UserRole[]>([]);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [rolesRefreshing, setRolesRefreshing] = useState(false);
    const [rolesError, setRolesError] = useState<string | null>(null);
    const [isRoleModalOpen, setRoleModalOpen] = useState(false);

    const appTargets = useMemo(() => getAppTargets(), []);

    useEffect(() => {
        if (!isRoleModalOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isRoleModalOpen]);

    useEffect(() => {
        let alive = true;

        setMeLoading(true);
        setMeError(null);
        setRolesLoading(true);
        setRolesError(null);

        const loadProfile = async () => {
            try {
                const response = await api.get<MeResponse>("/api/me");
                if (!alive) return;
                setMe(response);
            } catch (e: any) {
                if (!alive) return;
                setMeError(e?.message ?? "Ошибка загрузки профиля");
            } finally {
                if (alive) setMeLoading(false);
            }
        };

        const loadRoles = async () => {
            try {
                const response = await apiIdp.get<RolesResponse>("/api/roles");
                if (!alive) return;
                const rolesFromApi = Array.isArray(response?.roles) ? response.roles : [];
                setUserRoles(buildRoleList(rolesFromApi));
            } catch (e: any) {
                if (!alive) return;
                setRolesError(e?.message ?? "Не удалось загрузить роли");
                setUserRoles(buildRoleList([]));
            } finally {
                if (alive) setRolesLoading(false);
            }
        };

        loadProfile();
        loadRoles();

        return () => {
            alive = false;
        };
    }, []);

    const initials = useMemo(() => {
        const n = me?.name?.trim() || me?.phone || "—";
        const parts = (me?.name || "").trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return String(n).slice(-2).toUpperCase();
    }, [me]);

    const currentRoleName = useMemo(() => {
        if (me?.primaryRole) return roleDisplay[me.primaryRole] || me.primaryRole;

        const fallbackRole = userRoles.find(
            (role) => role.hasRole && CURRENT_APP_ROLE_CODES.includes(role.code)
        );

        return fallbackRole?.name || "Франчайзи";
    }, [me, userRoles]);

    const availableRoles = useMemo(
        () =>
            userRoles.filter(
                (role) => role.hasRole && !CURRENT_APP_ROLE_CODES.includes(role.code)
            ),
        [userRoles]
    );

    const canSwitchRole = availableRoles.length > 0;

    const handleRoleSelect = (roleCode: string) => {
        setRoleModalOpen(false);
        window.location.replace(resolveRoleTarget(roleCode, appTargets));
    };

    const handleRefreshRoles = async () => {
        try {
            setRolesRefreshing(true);
            setRolesError(null);

            const response = await refreshRolesJwt();
            const rolesFromApi = Array.isArray(response?.roles) ? response.roles : [];
            setUserRoles(buildRoleList(rolesFromApi));
            setRoleModalOpen(false);
        } catch (e: any) {
            setRolesError(e?.message ?? "Не удалось обновить роли");
        } finally {
            setRolesRefreshing(false);
        }
    };

    return (
        <div className="profile-page">
            <header className="profile-page__header">
                <button
                    className="profile-page__back"
                    type="button"
                    aria-label="Назад"
                    onClick={() => window.history.back()}
                >
                    ←
                </button>
                <h1 className="profile-page__title">Профиль</h1>
            </header>

            <main className="profile-page__content">
                <section className="profile-card">
                    <div className="profile-card__avatar-wrap">
                        {meLoading ? (
                            <div className="profile-card__avatar skeleton" />
                        ) : me?.avatarUrl ? (
                            <img
                                src={me.avatarUrl}
                                alt={me.name || "Профиль"}
                                className="profile-card__avatar"
                            />
                        ) : (
                            <div className="profile-card__avatar fallback">
                                {initials}
                            </div>
                        )}
                    </div>

                    <div className="profile-card__info">
                        <div className="profile-card__name">
                            {meLoading
                                ? "Загрузка…"
                                : me?.name || me?.phone || "Без имени"}
                        </div>

                        {me?.email && (
                            <div className="profile-card__email">
                                {me.email}
                            </div>
                        )}

                        <div className="profile-card__role">
                            Текущая роль:{" "}
                            <span className="profile-card__role-accent">
                                {currentRoleName}
                            </span>
                        </div>

                        {meError && (
                            <div className="profile-card__error">
                                {meError}
                            </div>
                        )}
                    </div>
                </section>

                <section className="profile-roles">
                    {rolesError && (
                        <div className="profile-roles__error">{rolesError}</div>
                    )}

                    {canSwitchRole && (
                        <button
                            type="button"
                            className="profile-roles__change-btn"
                            onClick={() => setRoleModalOpen(true)}
                            disabled={rolesLoading || rolesRefreshing}
                        >
                            {rolesLoading
                                ? "Загрузка ролей..."
                                : `Сменить роль (${availableRoles.length} доступно)`}
                        </button>
                    )}

                    <button
                        type="button"
                        className="profile-roles__refresh-btn"
                        onClick={handleRefreshRoles}
                        disabled={rolesLoading || rolesRefreshing}
                    >
                        {rolesRefreshing ? "Обновляем роли..." : "Обновить роли"}
                    </button>
                </section>

                {isRoleModalOpen && canSwitchRole && (
                    <div
                        className="profile-role-modal-backdrop"
                        onClick={() => setRoleModalOpen(false)}
                    >
                        <div
                            className="profile-role-modal"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <h2 className="profile-role-modal__title">Выберите роль</h2>

                            <div className="profile-role-modal__list">
                                <button
                                    type="button"
                                    className="profile-role-modal__item"
                                    onClick={() => handleRoleSelect(CURRENT_APP_ACTION_CODE)}
                                >
                                    {CURRENT_APP_STAY_LABEL}
                                </button>

                                {availableRoles.map((role) => (
                                    <button
                                        key={role.code}
                                        type="button"
                                        className="profile-role-modal__item"
                                        onClick={() => handleRoleSelect(role.code)}
                                    >
                                        {`Открыть как ${role.name}`}
                                    </button>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="profile-role-modal__cancel"
                                onClick={() => setRoleModalOpen(false)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ProfilePage;
